import { ClienteDto, DettaglioAzienda } from "@api/client";
import { companyUpdateCompany } from "common/_redux/company/actions";
import { companyDetailsSelector } from "common/_redux/company/selectors";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import BftInput from "common/components/input";
import BftRadioGroup from "common/components/radio-button";
import BftSelect from "common/components/select";
import { Formik, dettaglioAziendaSchema } from "common/validators/validators";
import { BooleanValues } from "constants/global";
import { areaGeograficaOptions, tipologiaAttivitaOptions } from "constants/options";
import { Settori } from "constants/regioni";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";

const { TRUE, FALSE } = BooleanValues;

const settori = Settori;

interface Props {
	onComplete: () => void;
}

const DettaglioSocieta: React.FC<Props> = (props) => {
	const { onComplete } = props;

	const dispatch = useAppDispatch();

	const companyDetails = useAppSelector(companyDetailsSelector);

	const _handleSubmit = (values: DettaglioAzienda) => {
		let request: ClienteDto = {
			...companyDetails,
			dettaglioAzienda: values,
		};

		dispatch(companyUpdateCompany(request))
			.unwrap()
			.then((res) => {
				if (res) onComplete();
			});
	};

	return (
		<Formik
			validationSchema={dettaglioAziendaSchema}
			onSubmit={_handleSubmit}
			initialValues={{
				tipologiaAttivita: companyDetails?.dettaglioAzienda?.tipologiaAttivita,
				tipologiaAttivitaDescrizione:
					companyDetails?.dettaglioAzienda?.tipologiaAttivitaDescrizione,
				settoreAttivita: companyDetails?.dettaglioAzienda?.settoreAttivita,
				settoreAttivitaDescrizione: companyDetails?.dettaglioAzienda?.settoreAttivitaDescrizione,
				numeroDipendenti: companyDetails?.dettaglioAzienda?.numeroDipendenti,
				fondiPubblici: false,
				tipoFondi: companyDetails?.dettaglioAzienda?.tipoFondi,
				areaGeografica: companyDetails?.dettaglioAzienda?.areaGeografica,
			}}
			enableReinitialize
		>
			{({ handleSubmit, handleChange, setFieldValue, values, touched, errors }) => {
				const handleChangeCustom = (input: string, type?: string) => (e: any) => {
					if (type === "amount") {
						const value = typeof e === "object" ? e.target?.value : e;
						const inputReplaced = value
							.toString()
							.replace(/[^0-9,]/g, "")
							.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
						setFieldValue(input, inputReplaced);
					} else if (type === "date") {
						setFieldValue(input, e);
					} else {
						setFieldValue(input, e === TRUE);
					}
					// handleChange(e);
				};

				const handleFieldChange = (field: string, value: string) => {
					if (field === "tipologiaAttivita") {
						setFieldValue("tipologiaAttivita", value);

						if (value === "noProfit") {
							setFieldValue("settoreAttivita", "AttivitaNoProfit");
						} else {
							setFieldValue("settoreAttivita", "");
						}
					}
					if (field === "settoreAttivita") {
						setFieldValue("settoreAttivita", value);

						if (value === "AttivitaNoProfit") {
							setFieldValue("tipologiaAttivita", "noProfit");
						}
					}
				};

				return (
					<Form noValidate onSubmit={handleSubmit}>
						<h5 className="container my-3">Attività</h5>

						<Row>
							<Col md={12}>
								<BftRadioGroup
									controlId="tipologiaAttivita"
									label="Indicare la tipologia di attività"
									options={tipologiaAttivitaOptions}
									// onChange={handleTipologiaChange}
									onChange={(e) => handleFieldChange("tipologiaAttivita", e.target.value)}
									selectedOption={values?.tipologiaAttivita}
									isInvalid={!!errors?.tipologiaAttivita && touched?.tipologiaAttivita}
								/>
								{errors?.tipologiaAttivita && touched?.tipologiaAttivita && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors?.tipologiaAttivita}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>

						{values.tipologiaAttivita === "altro" && (
							<Row>
								<Col md={8}>
									<BftInput
										required
										type={"text"}
										name={"tipologiaAttivitaDescrizione"}
										placeholder={"Specificare attività*"}
										value={values?.tipologiaAttivitaDescrizione?.toUpperCase()}
										onChange={(e) => {
											const { name, value } = e.target;
											setFieldValue(name, value.toUpperCase());
										}}
										isInvalid={
											!!errors?.tipologiaAttivitaDescrizione &&
											touched?.tipologiaAttivitaDescrizione
										}
									/>
									{errors?.tipologiaAttivitaDescrizione &&
										touched?.tipologiaAttivitaDescrizione && (
											<Form.Control.Feedback className="ms-3" type="invalid">
												{errors?.tipologiaAttivitaDescrizione}
											</Form.Control.Feedback>
										)}
								</Col>
							</Row>
						)}

						{values.tipologiaAttivita === "noProfit" && (
							<Row>
								<Col md={8}>
									<BftInput
										required
										type={"text"}
										name={"tipologiaAttivitaDescrizione"}
										placeholder={"Indicare i beneficiari dell'attività No Profit*"}
										value={values?.tipologiaAttivitaDescrizione?.toUpperCase()}
										onChange={(e) => {
											const { name, value } = e.target;
											setFieldValue(name, value.toUpperCase());
										}}
										isInvalid={
											!!errors?.tipologiaAttivitaDescrizione &&
											touched?.tipologiaAttivitaDescrizione
										}
									/>
									{errors?.tipologiaAttivitaDescrizione &&
										touched?.tipologiaAttivitaDescrizione && (
											<Form.Control.Feedback className="ms-3" type="invalid">
												{errors?.tipologiaAttivitaDescrizione}
											</Form.Control.Feedback>
										)}
								</Col>
							</Row>
						)}

						<h6 className="container my-3">Settore prelevante</h6>

						<Row>
							<Col md={8}>
								<BftSelect
									name={"settoreAttivita"}
									placeholder={"Settore"}
									value={values?.settoreAttivita}
									onChange={(e) => handleFieldChange("settoreAttivita", e.target.value)}
									disabled={values?.tipologiaAttivita === "noProfit"}
									isInvalid={!!errors?.settoreAttivita && touched?.settoreAttivita}
									options={settori}
								/>
								{errors?.settoreAttivita && touched?.settoreAttivita && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors?.settoreAttivita}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>

						{values?.settoreAttivita === "Altro" && (
							<Row>
								<Col md={8}>
									<BftInput
										required
										type={"text"}
										name={"settoreAttivitaDescrizione"}
										placeholder={"Specificare settore*"}
										value={values?.settoreAttivitaDescrizione?.toUpperCase()}
										onChange={(e) => {
											const { name, value } = e.target;
											setFieldValue(name, value.toUpperCase());
										}}
										isInvalid={
											!!errors?.settoreAttivitaDescrizione && touched?.settoreAttivitaDescrizione
										}
									/>
									{errors?.settoreAttivitaDescrizione && touched?.settoreAttivitaDescrizione && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors?.settoreAttivitaDescrizione}
										</Form.Control.Feedback>
									)}
								</Col>
							</Row>
						)}

						<h5 className="container my-3">Caratteristiche dell'attività d'impresa</h5>

						<Row>
							<Col md={8}>
								<BftInput
									required
									type={"text"}
									name={"numeroDipendenti"}
									placeholder={"N° dipendenti*"}
									value={values?.numeroDipendenti}
									onChange={handleChangeCustom("numeroDipendenti", "amount")}
									isInvalid={!!errors?.numeroDipendenti && touched?.numeroDipendenti}
								/>
								{errors?.numeroDipendenti && touched?.numeroDipendenti && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors?.numeroDipendenti}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>

						<BftBooleanButton
							label="Beneficia di fondi pubblici?"
							name="fondiPubblici"
							value={FALSE}
							onChange={handleChangeCustom("fondiPubblici")}
						/>
						{errors?.fondiPubblici && touched?.fondiPubblici && (
							<Form.Control.Feedback className="ms-3" type="invalid">
								{errors?.fondiPubblici}
							</Form.Control.Feedback>
						)}

						{values?.fondiPubblici && (
							<Row>
								<Col md={8}>
									<BftInput
										required
										type={"text"}
										name={"tipoFondi"}
										placeholder={"Tipo di fondi*"}
										value={values?.tipoFondi?.toUpperCase()}
										onChange={(e) => {
											const { name, value } = e.target;
											setFieldValue(name, value.toUpperCase());
										}}
										isInvalid={!!errors?.tipoFondi && touched?.tipoFondi}
									/>
									{errors?.tipoFondi && touched?.tipoFondi && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors?.tipoFondi}
										</Form.Control.Feedback>
									)}
								</Col>
							</Row>
						)}

						<h5 className="container my-3">Area geografica attività svolta</h5>

						<Row>
							<Col md={8}>
								<BftRadioGroup
									controlId="areaGeografica"
									label="Selezionare area*"
									options={areaGeograficaOptions}
									onChange={handleChange}
									isInvalid={!!errors?.areaGeografica && touched?.areaGeografica}
									selectedOption={values?.areaGeografica}
								/>
								{errors?.areaGeografica && touched?.areaGeografica && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors?.areaGeografica}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>

						<div className="mx-2 my-4">
							<BftButton type="submit" className="btn-buffetti col-12">
								Avanti
							</BftButton>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default DettaglioSocieta;
